import axios, { AxiosInstance, AxiosResponse, AxiosError } from 'axios';
import {
  AuthResponse,
  User,
  Case,
  Dialogue,
  AudioFile,
  AudioGenerationResponse,
  Rating,
  CreateCaseForm,
  CreateDialogueForm,
  ModifyDialogueForm,
  CreateRatingForm,
  ImproveDialogueForm,
  AudioGenerationRequest,
  DialogueModificationResponse,
  EmotionAnalysis,
  GeneratedQuestions,
  PaginatedResponse,
  GuidedLearningSession,
  MultiCharacterResponse,
  GuidanceResponse,
  GuidedLearningCheckpoint,
  CreateGuidedLearningSessionForm,
  SendMessageForm,
  CreateCheckpointForm,
  UpdateSessionStatusForm,
  LearnerLevel,
  CaseParameters,
  CreateCustomCasePayload,
  CustomCaseInfoPayload,
  SeedGenerationPayload,
  CalibrationItemDto,
  CalibrationProgress,
  CalibrationListItem,
  DialogueReviewAnswers,
  DialogueReviewItemDto,
  DialogueReviewListItem,
  DialogueReviewProgress,
} from '../types';

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5030/api',
      timeout: 180000, // 180 seconds (3 minutes) for AI generation operations
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('accessToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor to handle token refresh
    this.api.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as any;

        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            const refreshToken = localStorage.getItem('refreshToken');
            if (refreshToken) {
              const response = await axios.post(`${this.api.defaults.baseURL}/auth/refresh-token`, {
                refreshToken,
              });

              const { accessToken, refreshToken: newRefreshToken } = response.data;
              localStorage.setItem('accessToken', accessToken);
              localStorage.setItem('refreshToken', newRefreshToken);

              originalRequest.headers.Authorization = `Bearer ${accessToken}`;
              return this.api(originalRequest);
            }
          } catch (refreshError) {
            // Refresh failed, redirect to login
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            window.location.href = '/login';
          }
        }

        return Promise.reject(error);
      }
    );
  }

  // Auth endpoints
  async login(email: string, password: string): Promise<AuthResponse> {
    const response = await this.api.post('/auth/login', { email, password });
    return response.data;
  }

  async register(userData: {
    email: string;
    password: string;
    name: string;
    role?: string;
  }): Promise<AuthResponse> {
    const response = await this.api.post('/auth/register', userData);
    return response.data;
  }

  async getProfile(): Promise<User> {
    const response = await this.api.get('/auth/profile');
    return response.data;
  }

  async updateProfile(userData: { name?: string; email?: string }): Promise<User> {
    const response = await this.api.put('/auth/profile', userData);
    return response.data;
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<{ message: string }> {
    const response = await this.api.put('/auth/change-password', {
      currentPassword,
      newPassword,
    });
    return response.data;
  }

  async logout(): Promise<{ message: string }> {
    const response = await this.api.post('/auth/logout');
    return response.data;
  }

  // Cases endpoints
  async getCases(page: number = 1, limit: number = 10, search?: string): Promise<PaginatedResponse<Case>> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    if (search) params.append('search', search);

    const response = await this.api.get(`/cases?${params}`);
    return response.data;
  }

  async getCaseById(id: string): Promise<Case> {
    const response = await this.api.get(`/cases/${id}`);
    return response.data;
  }

  async getCaseByCaseId(caseId: string): Promise<Case> {
    const response = await this.api.get(`/cases/case-id/${caseId}`);
    return response.data;
  }

  async createCase(caseData: CreateCaseForm): Promise<Case> {
    const response = await this.api.post('/cases', caseData);
    return response.data;
  }

  async generateNewCase(): Promise<Case> {
    const response = await this.api.post('/cases/generate');
    return response.data;
  }

  async updateCase(id: string, caseData: Partial<CreateCaseForm>): Promise<Case> {
    const response = await this.api.put(`/cases/${id}`, caseData);
    return response.data;
  }

  async deleteCase(id: string): Promise<void> {
    await this.api.delete(`/cases/${id}`);
  }

  async makeUserCaseIntoTemplate(instanceId: string): Promise<any> {
    const response = await this.api.post(`/user-cases/${instanceId}/make-template`);
    return response.data;
  }

  async updateUserCaseInstance(instanceId: string, caseData: any): Promise<any> {
    const response = await this.api.put(`/user-cases/${instanceId}`, caseData);
    return response.data;
  }

  async continueDialogue(instanceId: string, speaker?: string, line?: string): Promise<any> {
    const response = await this.api.post(`/user-cases/${instanceId}/continue`, { speaker, line });
    return response.data;
  }

  async getCaseVersions(instanceId: string): Promise<any> {
    const response = await this.api.get(`/user-cases/${instanceId}/versions`);
    return response.data;
  }

  async rollbackToVersion(instanceId: string, versionNumber: number): Promise<any> {
    const response = await this.api.post(`/user-cases/${instanceId}/versions/${versionNumber}/rollback`);
    return response.data;
  }

  async getUserCaseInstances(userId?: string): Promise<any> {
    const params = userId ? { userId } : {};
    const response = await this.api.get('/user-cases', { params });
    return response.data;
  }

  async getUserCaseInstance(instanceId: string): Promise<any> {
    const response = await this.api.get(`/user-cases/${instanceId}`);
    return response.data;
  }

  async startCaseFromTemplate(masterCaseId: string): Promise<any> {
    const response = await this.api.post(`/user-cases/start/${masterCaseId}`);
    return response.data;
  }

  async startCaseFromMaster(masterCaseId: string): Promise<any> {
    // Alias for startCaseFromTemplate
    return this.startCaseFromTemplate(masterCaseId);
  }

  async deleteUserCaseInstance(instanceId: string): Promise<void> {
    await this.api.delete(`/user-cases/${instanceId}`);
  }

  async getMasterCases(): Promise<any> {
    const response = await this.api.get('/master-cases');
    return response.data;
  }

  async deleteMasterCase(caseId: string): Promise<void> {
    await this.api.delete(`/master-cases/${caseId}`);
  }

  async generateCaseInfoWithPrompt(prompt: string): Promise<any> {
    const response = await this.api.post('/master-cases/generate-case-info', { prompt });
    return response.data;
  }

  async createCustomCase(payload: CreateCustomCasePayload | CustomCaseInfoPayload): Promise<any> {
    const requestBody = (payload as CreateCustomCasePayload).caseInfo
      ? payload
      : {
          caseInfo: payload,
          personas: (payload as any).personas,
        };
    const response = await this.api.post('/user-cases/custom', requestBody);
    return response.data;
  }

  async getCaseParameters(): Promise<CaseParameters> {
    const response = await this.api.get('/case-parameters');
    return response.data;
  }

  async generateCaseWithParameters(params: any): Promise<any> {
    const response = await this.api.post('/master-cases/generate-with-parameters', params);
    return response.data;
  }

  // Calibration endpoints
  async getCalibrationItems(): Promise<{ items: CalibrationListItem[] }> {
    const response = await this.api.get('/calibration/items');
    return response.data;
  }

  async getNextCalibrationItem(): Promise<{
    done: boolean;
    item?: CalibrationItemDto;
    totalItems?: number;
    ratedCount?: number;
  }> {
    const response = await this.api.get('/calibration/items/next');
    return response.data;
  }

  async getCalibrationItem(itemId: string): Promise<{ item: CalibrationItemDto }> {
    const response = await this.api.get(`/calibration/items/${itemId}`);
    return response.data;
  }

  async submitCalibrationRating(
    itemId: string,
    payload: {
      emotionalAuthenticityScore: number;
      groundingScore: number;
      topicConsistencyScore: number;
      informationFogScore?: number;
      relationshipTextureScore?: number;
      notes?: string;
    }
  ): Promise<{ success: boolean }> {
    const response = await this.api.post(`/calibration/items/${itemId}/rating`, payload);
    return response.data;
  }

  async getMyCalibrationProgress(): Promise<CalibrationProgress> {
    const response = await this.api.get('/calibration/progress/me');
    return response.data;
  }

  // Dialogue Review endpoints (Rubric 2.0 on pre-generated dialogues)
  async getDialogueReviewItems(): Promise<{
    items: DialogueReviewListItem[];
    progress: DialogueReviewProgress;
  }> {
    const response = await this.api.get('/dialogue-review/items');
    return response.data;
  }

  async getDialogueReviewItem(slug: string): Promise<{ item: DialogueReviewItemDto }> {
    const response = await this.api.get(`/dialogue-review/items/${slug}`);
    return response.data;
  }

  async submitDialogueReviewResponse(
    slug: string,
    payload: { answers: DialogueReviewAnswers; overallComment?: string }
  ): Promise<{ success: boolean }> {
    const response = await this.api.post(`/dialogue-review/items/${slug}/response`, payload);
    return response.data;
  }

  // Pipeline-style generation from seeds (2-step process)
  async generateCaseInfoFromSeeds(seeds: SeedGenerationPayload): Promise<any> {
    const response = await this.api.post('/user-cases/generate-case-info', { seeds });
    return response.data;
  }

  async generateDialogueFromCaseInfo(
    caseInfo: CustomCaseInfoPayload,
    seeds: Partial<SeedGenerationPayload> | undefined,
    targetTurns: number
  ): Promise<any> {
    const response = await this.api.post('/user-cases/generate-dialogue', { caseInfo, seeds, targetTurns });
    return response.data;
  }

  // OLD: Single-step generation (deprecated)
  async generateCompleteCaseFromSeeds(seeds: any): Promise<any> {
    const response = await this.api.post('/user-cases/generate-from-seeds', { seeds });
    return response.data;
  }

  async saveReviewedCase(caseData: any, dialogue: any, seeds: any): Promise<any> {
    const response = await this.api.post('/user-cases/save-reviewed', { case: caseData, dialogue, seeds });
    return response.data;
  }

  async toggleGoldExample(caseId: string): Promise<any> {
    const response = await this.api.patch(`/master-cases/${caseId}/toggle-gold-example`);
    return response.data;
  }

  async toggleUserCaseGoldExample(instanceId: string): Promise<any> {
    const response = await this.api.patch(`/user-cases/${instanceId}/toggle-gold-example`);
    return response.data;
  }

  async setLearnerLevel(caseId: string, level: LearnerLevel): Promise<Case> {
    const response = await this.api.patch(`/user-cases/${caseId}/learner-level`, { learnerLevel: level });
    return response.data;
  }

  async setMasterCaseLearnerLevel(caseId: string, level: LearnerLevel): Promise<Case> {
    const response = await this.api.patch(`/master-cases/${caseId}/learner-level`, { learnerLevel: level });
    return response.data;
  }

  // Dialogue endpoints
  async getDialogueByCaseId(caseId: string): Promise<Dialogue> {
    const response = await this.api.get(`/dialogues/${caseId}`);
    return response.data;
  }

  async createDialogue(caseId: string, dialogueData: CreateDialogueForm): Promise<Dialogue> {
    const response = await this.api.post(`/dialogues/${caseId}`, dialogueData);
    return response.data;
  }

  async updateDialogue(caseId: string, dialogueData: Partial<CreateDialogueForm>): Promise<Dialogue> {
    const response = await this.api.put(`/dialogues/${caseId}`, dialogueData);
    return response.data;
  }

  async getDialogueModificationOptions(
    caseId: string,
    action: string,
    originalText: string,
    turnIndex?: number,
    followingTurnsCount?: number
  ): Promise<DialogueModificationResponse> {
    const params = new URLSearchParams({
      action,
      originalText,
      ...(turnIndex !== undefined && { turnIndex: turnIndex.toString() }),
      ...(followingTurnsCount !== undefined && { followingTurnsCount: followingTurnsCount.toString() })
    });
    const response = await this.api.get(`/dialogues/${caseId}/options?${params}`);
    return response.data;
  }

  async modifyDialogueTurn(
    caseId: string,
    turnIndex: number,
    modificationData: ModifyDialogueForm
  ): Promise<any> {
    // Backend expects turnIndex in the body, use POST /modify endpoint
    const response = await this.api.post(`/dialogues/${caseId}/modify`, {
      ...modificationData,
      turnIndex
    });
    return response.data;
  }

  async improveDialogue(caseId: string, improveData: ImproveDialogueForm): Promise<Dialogue> {
    const response = await this.api.post(`/dialogues/${caseId}/improve`, improveData);
    return response.data;
  }

  // Audio endpoints
  async generateAudio(caseId: string): Promise<AudioGenerationResponse> {
    const response = await this.api.post(`/audio/${caseId}/generate`, {}, {
      timeout: 300000, // 5 minutes timeout for audio generation
    });
    return response.data;
  }

  async getAudioFile(id: string): Promise<Blob> {
    const response = await this.api.get(`/audio/${id}`, {
      responseType: 'blob',
      timeout: 60000, // 1 minute timeout for audio file retrieval
    });
    return response.data;
  }

  async getAudioFilesByDialogue(caseId: string): Promise<AudioFile[]> {
    const response = await this.api.get(`/audio/dialogue/${caseId}`);
    return response.data;
  }

  async deleteAudioFile(id: string): Promise<void> {
    await this.api.delete(`/audio/${id}`);
  }

  // Emotion analysis endpoints
  async analyzeEmotion(caseId: string, turnIndex: number): Promise<EmotionAnalysis> {
    try {
      // Try GET first to check for cached analysis
      const response = await this.api.get(`/dialogues/${caseId}/turns/${turnIndex}/emotion`);
      return response.data;
    } catch (error: any) {
      // If 404 (no cache), trigger new analysis with POST
      if (error.response?.status === 404) {
        const response = await this.api.post(`/dialogues/${caseId}/turns/${turnIndex}/emotion`);
        return response.data;
      }
      // Re-throw other errors
      throw error;
    }
  }

  // Direct emotion analysis with dataset control (for comparison)
  async analyzeEmotionDirect(speaker: string, text: string, context: string, useDataset: boolean = true): Promise<EmotionAnalysis> {
    const response = await this.api.post('/emotions/analyze', { 
      speaker, 
      text, 
      context, 
      useDataset 
    });
    return response.data;
  }

  // Question generation endpoints
  async generateQuestions(caseId: string): Promise<GeneratedQuestions> {
    const response = await this.api.get(`/dialogues/${caseId}/questions`);
    return response.data;
  }

  // Rating endpoints
  async createRating(caseId: string, ratingData: CreateRatingForm): Promise<Rating> {
    const response = await this.api.post(`/ratings/${caseId}`, ratingData);
    return response.data;
  }

  async autoRateDialogue(caseId: string): Promise<Rating> {
    const response = await this.api.post(`/ratings/${caseId}/auto-rate`);
    return response.data;
  }

  async getRatingsByDialogue(caseId: string): Promise<Rating[]> {
    const response = await this.api.get(`/ratings/${caseId}`);
    return response.data;
  }

  async getRatingById(id: string): Promise<Rating> {
    const response = await this.api.get(`/ratings/rating/${id}`);
    return response.data;
  }

  async updateRating(id: string, ratingData: Partial<CreateRatingForm>): Promise<Rating> {
    const response = await this.api.put(`/ratings/${id}`, ratingData);
    return response.data;
  }

  async deleteRating(id: string): Promise<void> {
    await this.api.delete(`/ratings/${id}`);
  }

  // Health check
  async healthCheck(): Promise<{ status: string; timestamp: string; uptime: number }> {
    const response = await this.api.get('/health');
    return response.data;
  }

  // Guided Learning endpoints
  async createGuidedLearningSession(sessionData: CreateGuidedLearningSessionForm): Promise<GuidedLearningSession> {
    const response = await this.api.post('/guided-learning/sessions', sessionData);
    return response.data;
  }

  async getGuidedLearningSessions(): Promise<GuidedLearningSession[]> {
    const response = await this.api.get('/guided-learning/sessions');
    return response.data;
  }

  async getGuidedLearningSession(sessionId: string): Promise<GuidedLearningSession> {
    const response = await this.api.get(`/guided-learning/sessions/${sessionId}`);
    return response.data;
  }

  async sendGuidedLearningMessage(sessionId: string, messageData: SendMessageForm): Promise<MultiCharacterResponse> {
    const response = await this.api.post(`/guided-learning/sessions/${sessionId}/messages`, messageData);
    return response.data;
  }

  async getGuidedLearningGuidance(sessionId: string, difficultyLevel?: string): Promise<GuidanceResponse> {
    const params = difficultyLevel ? `?difficultyLevel=${difficultyLevel}` : '';
    const response = await this.api.get(`/guided-learning/sessions/${sessionId}/guidance${params}`);
    return response.data;
  }

  async createGuidedLearningCheckpoint(sessionId: string, checkpointData: CreateCheckpointForm): Promise<GuidedLearningCheckpoint> {
    const response = await this.api.post(`/guided-learning/sessions/${sessionId}/checkpoints`, checkpointData);
    return response.data;
  }

  async updateGuidedLearningSessionStatus(sessionId: string, statusData: UpdateSessionStatusForm): Promise<{ message: string }> {
    const response = await this.api.put(`/guided-learning/sessions/${sessionId}/status`, statusData);
    return response.data;
  }

  async deleteGuidedLearningSession(sessionId: string): Promise<{ message: string }> {
    const response = await this.api.delete(`/guided-learning/sessions/${sessionId}`);
    return response.data;
  }

  async resumeGuidedLearningFromCheckpoint(sessionId: string, checkpointId: string): Promise<GuidedLearningSession> {
    const response = await this.api.post(`/guided-learning/sessions/${sessionId}/checkpoints/${checkpointId}/resume`);
    return response.data;
  }

  async getGuidedLearningTimeline(sessionId: string): Promise<{
    messages: any[];
    checkpoints: any[];
    navigationPoints: any[];
  }> {
    const response = await this.api.get(`/guided-learning/sessions/${sessionId}/timeline`);
    return response.data;
  }

  async getGuidedLearningAnalytics(sessionId: string): Promise<{
    progress: any;
    learningMetrics: any;
    characterAnalysis: any[];
  }> {
    const response = await this.api.get(`/guided-learning/sessions/${sessionId}/analytics`);
    return response.data;
  }

  async getGuidedLearningSummary(sessionId: string): Promise<{
    session: any;
    keyMetrics: any;
    highlights: string[];
    recommendations: string[];
  }> {
    const response = await this.api.get(`/guided-learning/sessions/${sessionId}/summary`);
    return response.data;
  }


  // Emotion Analysis Comparison (for testing purposes)
  async compareEmotionAnalysis(text: string, speaker?: string, context?: string): Promise<any> {
    const response = await this.api.post('/emotions/dataset/compare', { 
      text, 
      speaker: speaker || 'Patient', 
      context: context || 'Medical Consultation' 
    });
    return response.data;
  }

  async exportGuidedLearningSession(sessionId: string, format: 'json' | 'csv' | 'pdf' = 'json'): Promise<Blob> {
    const response = await this.api.get(`/guided-learning/sessions/${sessionId}/export?format=${format}`, {
      responseType: 'blob'
    });
    return response.data;
  }

  // Emotion voting methods
  async voteOnEmotion(voteData: {
    caseId: string;
    turnIndex: number;
    emotion: string;
    vote: 'up' | 'down' | 'neutral';
    aiScore: number;
  }): Promise<any> {
    const response = await this.api.post('/emotion-votes/vote', voteData);
    return response.data;
  }

  async getEmotionVotes(caseId: string, turnIndex: number): Promise<any> {
    const response = await this.api.get(`/emotion-votes/${caseId}/${turnIndex}`);
    return response.data;
  }

  async getEmotionVoteStats(): Promise<any> {
    const response = await this.api.get('/emotion-votes/stats');
    return response.data;
  }


  async getCachedEmotionAnalysis(caseId: string, turnIndex: number): Promise<any> {
    const response = await this.api.get(`/dialogues/${caseId}/turns/${turnIndex}/emotion`);
    return response.data;
  }

  async clearEmotionAnalysisCache(caseId: string, turnIndex: number): Promise<any> {
    const response = await this.api.delete(`/emotion-votes/clear/${caseId}/${turnIndex}`);
    return response.data;
  }

  // Dialogue voting methods
  async voteOnDialogue(voteData: {
    caseId: string;
    turnIndex: number;
    vote: 'up' | 'down' | 'neutral';
  }): Promise<any> {
    const response = await this.api.post('/dialogue-votes/vote', voteData);
    return response.data;
  }

  async getDialogueVotes(caseId: string, turnIndex: number): Promise<any> {
    const response = await this.api.get(`/dialogue-votes/${caseId}/${turnIndex}`);
    return response.data;
  }

  async getDialogueVoteStats(): Promise<any> {
    const response = await this.api.get('/dialogue-votes/stats');
    return response.data;
  }

  async clearDialogueVoteCache(caseId: string, turnIndex: number): Promise<any> {
    const response = await this.api.delete(`/dialogue-votes/clear/${caseId}/${turnIndex}`);
    return response.data;
  }

  // User emotion report methods
  async submitUserEmotionReport(reportData: {
    caseId: string;
    turnIndex: number;
    userEmotions: {
      gratitude: number;
      sadness: number;
      anger: number;
      fear: number;
    };
  }): Promise<any> {
    const response = await this.api.post('/user-emotion-reports/submit', reportData);
    return response.data;
  }

  async getUserEmotionReport(caseId: string, turnIndex: number): Promise<any> {
    const response = await this.api.get(`/user-emotion-reports/${caseId}/${turnIndex}`);
    return response.data;
  }

  async getCombinedEmotionAnalysis(caseId: string, turnIndex: number): Promise<any> {
    const response = await this.api.get(`/user-emotion-reports/combined/${caseId}/${turnIndex}`);
    return response.data;
  }

  async clearUserEmotionReportCache(caseId: string, turnIndex: number): Promise<any> {
    const response = await this.api.delete(`/user-emotion-reports/clear/${caseId}/${turnIndex}`);
    return response.data;
  }

  async analyzeAllEmotions(caseId: string, onProgress?: (progress: any) => void): Promise<any> {
    // Start the analysis
    await this.api.post(`/emotion-votes/start-analysis/${caseId}`);
    
    // Poll for progress
    return new Promise((resolve, reject) => {
      const pollInterval = setInterval(async () => {
        try {
          const response = await this.api.get(`/emotion-votes/progress/${caseId}`);
          const progress = response.data.progress;
          
          if (onProgress) {
            onProgress({
              current: progress.current,
              total: progress.total,
              message: progress.message
            });
          }
          
          // Check if analysis is completed
          if (progress.message === 'Analysis completed' && progress.results) {
            clearInterval(pollInterval);
            resolve({
              success: true,
              caseId: caseId,
              results: progress.results
            });
          } else if (progress.message === 'Analysis failed') {
            clearInterval(pollInterval);
            reject(new Error(progress.error || 'Analysis failed'));
          }
        } catch (error) {
          clearInterval(pollInterval);
          reject(error);
        }
      }, 2000); // Poll every 2 seconds
      
      // Timeout after 10 minutes
      setTimeout(() => {
        clearInterval(pollInterval);
        reject(new Error('Analysis timed out'));
      }, 600000);
    });
  }

  // Assignment endpoints (for admin and instructors)
  async assignStudentToInstructor(instructorId: string, studentId: string): Promise<any> {
    const response = await this.api.post('/assignments', { instructorId, studentId });
    return response.data;
  }

  async removeStudentFromInstructor(assignmentId: string): Promise<void> {
    await this.api.delete(`/assignments/${assignmentId}`);
  }

  async getInstructorStudents(instructorId: string): Promise<any[]> {
    const response = await this.api.get(`/assignments/instructor/${instructorId}/students`);
    return response.data;
  }

  async getStudentInstructors(studentId: string): Promise<any[]> {
    const response = await this.api.get(`/assignments/student/${studentId}/instructors`);
    return response.data;
  }

  async getAllAssignments(): Promise<any[]> {
    const response = await this.api.get('/assignments/all');
    return response.data;
  }

  async getStudentCases(studentId: string): Promise<any[]> {
    const response = await this.api.get(`/assignments/student/${studentId}/cases`);
    return response.data;
  }

  // Get all users (for admin/instructor to see available students/instructors)
  async getAllUsers(): Promise<User[]> {
    const response = await this.api.get('/auth/users');
    return response.data;
  }

  // ============================================================================
  // Model Evaluation API
  // ============================================================================

  async getEvalCases(sourceType?: 'gold' | 'synthetic', page = 1, limit = 50, promptVariant?: string): Promise<{
    cases: any[];
    pagination: { page: number; limit: number; total: number; totalPages: number };
  }> {
    const params = new URLSearchParams();
    if (sourceType) params.append('sourceType', sourceType);
    if (promptVariant) params.append('promptVariant', promptVariant);
    params.append('page', page.toString());
    params.append('limit', limit.toString());

    const response = await this.api.get(`/evaluation/cases?${params}`);
    return response.data;
  }

  async getEvalCasesByLogicalCase(sourceType: 'gold' | 'synthetic', sampleId: number): Promise<{ cases: any[] }> {
    const params = new URLSearchParams({ sourceType, sampleId: sampleId.toString() });
    const response = await this.api.get(`/evaluation/cases/by-logical?${params}`);
    return response.data;
  }

  async getEvalCase(id: string): Promise<any> {
    const response = await this.api.get(`/evaluation/cases/${id}`);
    return response.data;
  }

  async getEvalTurnResponses(turnId: string): Promise<any> {
    const response = await this.api.get(`/evaluation/turns/${turnId}/responses`);
    return response.data;
  }

  async submitEvalRankings(turnId: string, rankings: {
    best: Array<{ id: string; comment?: string }>;
    worst: Array<{ id: string; comment?: string }>;
  }): Promise<{ success: boolean; message: string }> {
    const response = await this.api.post(`/evaluation/turns/${turnId}/rankings`, { rankings });
    return response.data;
  }

  async getMyEvalRankings(turnId: string): Promise<{
    best: Array<{ id: string; position: number; comment: string | null }>;
    worst: Array<{ id: string; position: number; comment: string | null }>;
  }> {
    const response = await this.api.get(`/evaluation/turns/${turnId}/my-rankings`);
    return response.data;
  }

  async getEvalTurnStats(turnId: string): Promise<{
    stats: Array<{
      modelName: string;
      evalModelResponseId: string;
      bestCount: number;
      worstCount: number;
      score: number;
      totalVotes: number;
    }>;
  }> {
    const response = await this.api.get(`/evaluation/turns/${turnId}/stats`);
    return response.data;
  }

  async submitEvalCaseFeedback(caseId: string, feedback: {
    rubricSuggestions?: string;
  }): Promise<{ success: boolean }> {
    const response = await this.api.post(`/evaluation/cases/${caseId}/feedback`, feedback);
    return response.data;
  }

  async getEvalCaseFeedback(caseId: string): Promise<{
    rubricSuggestions: string;
  }> {
    const response = await this.api.get(`/evaluation/cases/${caseId}/feedback`);
    return response.data;
  }
}

export default new ApiService();
